import React from 'react';
import Link from 'next/link';
import CookieDeclaration from 'components/CookieDeclaration';
import Line from 'components/Line';
import Layout1x1 from 'components/Layout1x1';
import NextSteps from 'components/NextSteps';
import SalesCalloutHTO from 'components/SalesCalloutHTO';
import SalesCalloutSwatches from 'components/SalesCalloutSwatches';
import DoubleHeader from 'components/typography/DoubleHeader';
import { withPageStaticProps } from 'utils/header';

class Privacy extends React.Component {
  render() {
    return (
      <>
        <Layout1x1 className="m-section-hero">
          <DoubleHeader hero smallTitle={<h2>Revised: May 29, 2026</h2>} largeTitle={<h1>Privacy Policy</h1>} />

          <Line />

          <p>
            Your information privacy is extremely important to us. We have prepared this Privacy Policy to explain how we collect, use,
            protect, and disclose information and data when you use the Generation Tux website or the Generation Tux mobile application
            (“Site”) or Generation Tux products and services (“Services”). This Privacy Policy also explains your choices for managing your
            information preferences, including opting out of certain uses of your Personal Information. This Privacy Policy applies to all
            users of the Site and Services. The Site and Services are provided by Generation Tux, Inc. (“Generation Tux” “we” “our” “us”).
          </p>

          <p>BY USING THE SITE OR SERVICES, YOU ARE CONSENTING TO THIS PRIVACY POLICY. PLEASE READ IT CAREFULLY.</p>

          <ol className="space-y-32">
            <li className="space-y-32">
              <p>
                <strong>Managing Your Information Preferences</strong>
              </p>

              <p>
                You can review, correct, update, or change your Personal Information (defined below) or opt out of receiving certain e-mails
                by changing the relevant settings in your Generation Tux account or by contacting{' '}
                <Link href="/support">Customer Experience</Link>.
              </p>
              <p>
                You may manage your communication preferences at any time through your Generation Tux account. Promotional communications
                include special offers, tips, and other marketing content; you may opt out of these at any time. You must receive
                transactional communications, like order confirmations, shipping updates, and account notifications, as part of the
                Services.
              </p>
              <p>
                If you have opted in to receive SMS messages from Generation Tux, you may opt out at any time by replying STOP to any
                message or by updating your communication preferences in your account. Standard message and data rates may apply. For more
                details on how we handle SMS data, please see our SMS-specific privacy disclosures below.{' '}
              </p>
              <p>
                <strong>1(b). SMS/Text Messaging </strong>
              </p>
              <p>
                If you opt in to receive text messages from Generation Tux, you consent to receive automated promotional and/or
                transactional text messages at the mobile number you provide. Message frequency may vary. Standard message and data rates
                may apply.{' '}
              </p>
              <p>
                You may opt out of SMS messages at any time by replying STOP to any message. For help, contact Customer Experience. Your
                consent to receive SMS messages is not a condition of any purchase. We will not share your mobile phone number with third
                parties for their marketing purposes without your separate consent.{' '}
              </p>
            </li>

            <li className="space-y-32">
              <p>
                <strong>Information We Collect</strong>
              </p>

              <h4 className="text-h4 text-gray-dark">PERSONAL INFORMATION</h4>

              <p>
                We collect information that personally identifies you, such as your name, address, phone number, mobile phone number,
                e-mail, payment information, and other personally identifiable information that you choose to provide us with or that you
                choose to include in your Generation Tux account (“Personal Information”). You may be asked to provide us with Personal
                Information when you register with the Site or use any of our Services, and at other times. The decision to provide this
                information is optional; however, if you choose not to provide the requested information, you may not be able to use some or
                all of the features of the Site or Services. When someone creates a new event, the event creator will share Personal
                Information about the participants, including names, e-mail addresses, and phone numbers, with Generation Tux. We may also
                receive Personal Information, including your name, e-mail address, phone number, and social network ID from social media
                websites and services if you chose to enable those features.
              </p>

              <h4 className="text-h4 text-gray-dark">USAGE DATA AND SITE ACTIVITY</h4>

              <p>
                We automatically collect information regarding the actions you take on the Site (“Usage Data”). For example, each time you
                use the Site we automatically collect the type of Web browser you use, your operating system, your Internet Service
                Provider, your IP address, the pages you view, and the time and duration of your visits to the Site. We use this information
                to help us understand how people use the Site and Services, and to enhance the services we offer.
              </p>

              <h4 className="text-h4 text-gray-dark">COOKIES AND WEB BEACONS</h4>

              <p>
                We use cookies (a small text file placed on your computer to identify your computer and web browser). We do not use cookies
                to collect Personal Information. Most web browsers are initially set up to accept cookies. You can reset your web browser to
                refuse all cookies or to indicate when a cookie is being sent, however, certain features of the Site or Services may not
                work if you delete or disable cookies. Some of our Service Providers may use their own cookies, web beacons, or other
                tracking technology in connection with the services they perform on our behalf.
              </p>

              <p>
                We use Google Analytics on the Site and Services to collect Usage Data, to analyze how users use the Site and Services, and
                to provide advertisements to you on other websites. We also use Google Analytics Advertising Features which allows us to
                view and analyze demographic information collected by Google’s Interest-based advertising or third party audience data (such
                as age, gender, and interests) as well as to combine that information with information from Google Analytics. For more
                information about how to opt out of having your information used by Google Analytics, visit{' '}
                <a href="https://tools.google.com/dlpage/gaoptout/" target="_blank">
                  https://tools.google.com/dlpage/gaoptout/
                </a>
                . You can learn more about advertising and online tracking and how to opt out of such practices in the “Advertising and
                Online Tracking” section below.
              </p>
            </li>

            <li className="space-y-32">
              <p>
                <strong>How We Use Information and When We May Share and Disclose Information Generally</strong>
              </p>

              <p>
                We use personal information for internal purposes, such as to provide you with the site and services, improve them, and
                communicate with you about Generation Tux. These communications include: (a) Transactional messages, such as order
                confirmations, shipping notifications, fit profile updates, event reminders, and other account-related alerts that are
                necessary to fulfill your use of the Services; and (b) Promotional messages, like special offers, styling tips, product
                announcements, and other marketing content, are sent only with your consent and can be opted out of at any time.
              </p>

              <blockquote>
                We may also use your Personal Information to provide more relevant search results from the search functions of the Site and
                the Services. We will not disclose Personal Information to third parties, including for their direct marketing purposes,
                without your consent, except as explained in this Privacy Policy. We may disclose to third parties, certain Usage Data
                regarding the Site and Services. However, in such cases, your Usage Data is aggregated with the usage data of others and
                does not identify you individually.
              </blockquote>
            </li>

            <li className="space-y-32">
              <p>
                <strong>Personal Information You Make Available to Others</strong>
              </p>

              <p>
                By using the Site or the Services you may make certain of your Personal Information available to others. For example, when
                you post a comment or a photo on the Site other users may see your name (first name and last name initial), the city you
                live in and other information that you choose to make available to others.
              </p>
            </li>

            <li className="space-y-32">
              <p>
                <strong>Service Providers</strong>
              </p>

              <p>
                From time to time, we may establish a business relationship with other businesses whom we believe trustworthy and who have
                confirmed that their privacy practices are consistent with ours (“Service Providers”). For example, we may contract with
                Service Providers to provide certain services, such as hosting and maintenance, data storage and management, and marketing
                and promotions. We only provide our Service Providers with the information necessary for them to perform these services on
                our behalf. Each Service Provider must agree to use reasonable security procedures and practices, appropriate to the nature
                of the information involved, in order to protect your Personal Information from unauthorized access, use, or disclosure.
                Service Providers are prohibited from using Personal Information other than as specified by Generation Tux.
              </p>
            </li>

            <li className="space-y-32">
              <p>
                <strong>Referring Partners</strong>
              </p>

              <p>
                One or more of our business partners may have referred you to us for the Services (each a “Referring Partner”). We may
                provide your Personal Information to such Referring Partner. The Personal Information that we provide to each Referring
                Partner will be limited to your name and email address, which will be used for payment verification and reconciliation
                purposes. Any such Personal Information that is disclosed to a Referring Partner will be subject to their privacy policies.
                We will not disclose your Personal Information to non-Referring Partners except as permitted by applicable law.
              </p>
            </li>

            <li className="space-y-32">
              <p>
                <strong>Advertising and Online Tracking</strong>
              </p>

              <p>
                We may allow third-party companies to serve ads and collect certain anonymous information when you visit the Site. These
                companies may use non-personally identifiable information (e.g., click stream information, web browser type, time and date,
                subject of advertisements clicked or scrolled over) during your visits to the Site and other websites in order to provide
                advertisements about goods and services likely to be of interest to you. These companies typically use a cookie or
                third-party web beacon to collect this information. Our systems do not recognize browser “Do Not Track” signals, but several
                of our Service Providers who utilize these cookies or web beacons on our Site enable you to opt out of targeted advertising
                practices. To learn more about these advertising practices or to opt out of this type of advertising, you can visit{' '}
                <a href="http://www.networkadvertising.org" target="_blank">
                  www.networkadvertising.org
                </a>{' '}
                or{' '}
                <a href="http://www.aboutads.info/choices/" target="_blank">
                  www.aboutads.info/choices/
                </a>
                . We also provide you with additional tools to opt out of marketing from us. You can learn about this in the “Managing Your
                Information Preferences” section of this Privacy Policy.
              </p>

              <p>
                <strong>7(b). Communications </strong>
              </p>

              <p>
                By creating a Generation Tux account, you agree to receive transactional communications related to your account, orders,
                events, and rentals. These messages are a necessary part of the Services and cannot be opted out of.{' '}
              </p>

              <p>
                You may also choose to receive promotional communications from Generation Tux, including special offers, styling tips, and
                other marketing content, via email and/or SMS. You may opt in or out of promotional communications at any time through your
                account’s communication preferences or by following the unsubscribe instructions included in each message.{' '}
              </p>

              <p>
                For more details on how we collect, use, and manage your communication preferences, please refer to our Privacy Policy.{' '}
              </p>
            </li>

            <li className="space-y-32">
              <p>
                <strong>Other Transfers</strong>
              </p>

              <p>
                We may share Personal Information and Usage Data with businesses controlling, controlled by, or under common control with
                Generation Tux. If Generation Tux is merged, acquired, or sold, or in the event of a transfer of some or all of our assets,
                we may disclose or transfer Personal Information and Usage Data in connection with such transaction. You will have the
                opportunity to opt out of any such transfer if, in our discretion, the new entity plans to handle your information in a way
                that differs materially from this Privacy Policy.
              </p>
            </li>

            <li className="space-y-32">
              <p>
                <strong>Compliance with Laws and Law Enforcement</strong>
              </p>

              <p>
                Generation Tux cooperates with government and law enforcement officials and private parties to enforce and comply with the
                law. We may disclose Personal Information and any other information about you to government or law enforcement officials or
                private parties if, in our discretion, we believe it is necessary or appropriate in order to respond to legal requests
                (including court orders and subpoenas), to protect the safety, property, or rights of Generation Tux or of any third party,
                to prevent or stop any illegal, unethical, or legally actionable activity, or to comply with the law.
              </p>
            </li>

            <li className="space-y-32">
              <p>
                <strong>Be Careful When You Share Information with Others</strong>
              </p>

              <p>
                Please be aware that whenever you share information on any public area of the Site, that information may be accessed by
                others. In addition, please remember that when you share information in any other communications with third parties, that
                information may be passed along or made public by others. This means that anyone with access to such information can
                potentially use it for any purpose, including sending unsolicited communications.
              </p>
            </li>

            <li className="space-y-32">
              <p>
                <strong>Security</strong>
              </p>

              <p>
                We maintain physical, electronic, and procedural safeguards to protect the confidentiality and security of information
                transmitted to us. However, no data transmission over the Internet or other network can be guaranteed to be 100% secure. As
                a result, while we strive to protect information transmitted on or through the Site or Services, we cannot and do not
                guarantee the security of any information you transmit on or through the Site or Services, and you do so at your own risk.
              </p>
            </li>

            <li className="space-y-32">
              <p>
                <strong>Links To Other Websites</strong>
              </p>

              <p>
                Our Site and Services may contain links to other websites or allow others to send you such links. A link to a third party’s
                website does not mean that we endorse it or that we are affiliated with it. We do not exercise control over third-party
                websites. You access such third-party websites or content at your own risk. You should always read the privacy policy of a
                third-party website before providing any information to the website.
              </p>
            </li>

            <li className="space-y-32">
              <p>
                <strong>Children’s Privacy</strong>
              </p>

              <p>
                The Site and Services are intended for users who are 13 years old or older. We do not knowingly collect Personal Information
                from children under the age of 13. If we become aware that we have inadvertently received Personal Information from a child
                under the age of 13, we will delete such information from our records.
              </p>
            </li>

            <li className="space-y-32">
              <p>
                <strong>Processing in the United States</strong>
              </p>

              <p>
                Please be aware that your Personal Information and communications may be transferred to and maintained on servers or
                databases located outside your state, province, or country. If you are located outside of the United States, please be
                advised that we process and store all information in the United States. The laws in the United States may not be as
                protective of your privacy as those in your location. By using the Site or Services, you are agreeing to the collection,
                use, transfer, and disclosure of your Personal Information and communications will be governed by the applicable laws in the
                United States.
              </p>
            </li>

            <li className="space-y-32">
              <p>
                <strong>Privacy Policy Changes</strong>
              </p>

              <p>
                We may change this Privacy Policy from time to time. If we decide to change this Privacy Policy, we will inform you by
                posting the revised Privacy Policy on the Site. Those changes will go into effect on the “Revised” date shown in the revised
                Privacy Policy. By continuing to use the Site or Services, you are consenting to the revised Privacy Policy.
              </p>
            </li>
          </ol>

          <h2 className="text-h2">Your California Privacy Rights</h2>

          <p>
            If you are a California resident, under California law, including the California Consumer Privacy Act (CCPA), you have the
            rights outlined <Link href="/ccpa">here</Link>.
          </p>

          <p>
            <strong>
              PLEASE PRINT A COPY OF THIS PRIVACY POLICY FOR YOU RECORDS AND PLEASE CHECK THE SITE FREQUENTLY FOR ANY CHANGES.
            </strong>
          </p>

          <h2 className="text-h2">Cookie Declaration</h2>

          <CookieDeclaration />
        </Layout1x1>
        <NextSteps
          ctaPageName="privacy"
          salesCallout1={<SalesCalloutHTO ctaPageName="privacy" />}
          salesCallout2={<SalesCalloutSwatches ctaPageName="privacy" />}
        />
      </>
    );
  }
}

export const getStaticProps = withPageStaticProps();

export default Privacy;
